/**
 * „Daten unvollständig"-Banner (R1, E2) — persistenter Hinweis oberhalb des
 * View-Inhalts, wenn ein Endpoint sein Ergebnis mit `warning` (z. B. Code
 * PARTIAL_DATA) ausliefert: eine Neben-Quelle der View ist degradiert, die
 * angezeigten Zahlen sind ggf. unvollständig.
 *
 * Anders als die Toasts (api/errors.ts, 5 s ephemer) bleibt das Banner stehen,
 * bis der User es schließt oder die Route wechselt. Verdrahtung:
 *   - api/client.ts feuert `api:warning` je Envelope-warning,
 *   - router.ts feuert `route:willrender` vor jedem View-Render (→ Banner weg).
 * Einmalig via initDataBanner() im App-Bootstrap aktiviert; Views brauchen
 * KEINEN eigenen Code.
 */

import "../styles/data-banner.css";

type ApiWarning = { code: string; message?: string };

const HOST_ID = "data-banner-host";
let seen = new Set<string>();

function ensureHost(): HTMLElement {
  let host = document.getElementById(HOST_ID);
  if (!host) {
    host = document.createElement("div");
    host.id = HOST_ID;
    host.setAttribute("role", "alert");
    document.body.appendChild(host);
  }
  return host;
}

function clearBanner(): void {
  document.getElementById(HOST_ID)?.remove();
  seen = new Set();
}

function showWarning(endpoint: string, warning: ApiWarning): void {
  // Dedupe je View: derselbe Endpoint+Code erzeugt keine zweite Zeile.
  const key = `${endpoint}|${warning.code}`;
  if (seen.has(key)) return;
  seen.add(key);

  const host = ensureHost();
  const line = document.createElement("div");
  line.className = "data-banner__line";
  const label = warning.code === "PARTIAL_DATA" ? "Daten unvollständig" : warning.code;
  const msg = warning.message ? ` — ${warning.message}` : "";
  const text = document.createElement("span");
  text.textContent = `⚠ ${label}${msg} (${endpoint.replace("/api/v1/", "")})`;
  const close = document.createElement("button");
  close.className = "data-banner__close";
  close.setAttribute("aria-label", "Hinweis schließen");
  close.textContent = "×";
  close.addEventListener("click", () => {
    line.remove();
    const h = document.getElementById(HOST_ID);
    if (h && h.childElementCount === 0) clearBanner();
  });
  line.append(text, close);
  host.appendChild(line);
}

/** Einmalig im App-Bootstrap aufrufen. Idempotent. */
let initialized = false;
export function initDataBanner(): void {
  if (initialized) return;
  initialized = true;
  window.addEventListener("api:warning", (ev) => {
    const d = (ev as CustomEvent).detail as { endpoint?: string; warning?: ApiWarning } | undefined;
    if (d?.warning?.code) showWarning(String(d.endpoint ?? ""), d.warning);
  });
  window.addEventListener("route:willrender", clearBanner);
}
